/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, CheckCircle2, XCircle, Clock, Eye, MessageSquare, ChevronDown, ChevronUp, Filter, Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Pending Review', icon: Clock },
  approved: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Approved', icon: CheckCircle2 },
  declined: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Declined', icon: XCircle },
};

const BlogReviewPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    fetchSubmissions();
     
  }, [filter]);

  const fetchSubmissions = async () => {
    try {
      const url = filter ? `${API}/admin/blog/submissions?status=${filter}` : `${API}/admin/blog/submissions`;
      const res = await axios.get(url, { withCredentials: true });
      setSubmissions(res.data);
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (id, status) => {
    setProcessing(prev => ({ ...prev, [id]: true }));
    try {
      const params = new URLSearchParams({ status });
      if (feedback[id]) params.append('feedback', feedback[id]);
      await axios.put(`${API}/admin/blog/submissions/${id}?${params}`, {}, { withCredentials: true });
      toast.success(`Blog ${status === 'approved' ? 'approved and published' : 'declined'}!`);
      setFeedback(prev => ({ ...prev, [id]: '' }));
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setProcessing(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleDeleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog submission?')) return;
    try {
      await axios.delete(`${API}/admin/blog-submissions/${id}`, { withCredentials: true });
      toast.success('Submission deleted');
      fetchSubmissions();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const pendingCount = submissions.filter(s => s.status === 'pending').length;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-blog-review">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#002249]">Blog Review</h1>
          <p className="text-slate-600 text-sm mt-1">Review and approve member blog submissions.</p>
        </div>
        {pendingCount > 0 && filter !== 'pending' && (
          <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-sm text-sm font-semibold">{pendingCount} pending</span>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-2 mb-6">
        <Filter className="w-4 h-4 text-slate-500" />
        {['pending', 'approved', 'declined', ''].map(f => (
          <button
            key={f}
            onClick={() => { setFilter(f); setLoading(true); }}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${filter === f ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}`}
            data-testid={`filter-${f || 'all'}`}
          >
            {f === '' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg font-medium">No {filter} submissions</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((sub, index) => {
            const status = statusConfig[sub.status] || statusConfig.pending;
            const isExpanded = expandedId === sub.id;
            return (
              <motion.div
                key={sub.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * index }}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden"
                data-testid={`blog-submission-${index}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-[#002249] truncate">{sub.title}</h3>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm border flex items-center flex-shrink-0 ${status.color}`}>
                          <status.icon className="w-3 h-3 mr-1" />{status.label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-2">{sub.excerpt}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span>By <strong>{sub.author_name}</strong></span>
                        <span>{sub.author_email}</span>
                        <span>{new Date(sub.created_at).toLocaleDateString()}</span>
                        <span className="capitalize bg-slate-100 px-1.5 py-0.5 rounded">{sub.category?.replace(/_/g, ' ')}</span>
                      </div>
                      {sub.reviewed_at && (
                        <p className="text-xs text-slate-400 mt-1">Reviewed by {sub.reviewer_name} on {new Date(sub.reviewed_at).toLocaleDateString()}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteSubmission(sub.id); }} className="text-red-400 hover:text-red-600" data-testid={`delete-submission-${index}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <button onClick={() => setExpandedId(isExpanded ? null : sub.id)} className="text-slate-400 hover:text-slate-600 p-1" data-testid={`expand-${index}`}>
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-200">
                    {/* Full content */}
                    <div className="p-5 bg-slate-50">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Full Content</h4>
                      <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap bg-white p-4 border border-slate-200 rounded-sm max-h-[400px] overflow-y-auto">
                        {sub.content}
                      </div>
                      {sub.image_url && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-slate-500 mb-1">Featured Image:</p>
                          <Img variant="generic" src={resolveImg(sub.image_url)} alt="Blog" className="max-w-xs rounded-sm border" />
                        </div>
                      )}
                    </div>

                    {/* Review actions */}
                    {sub.status === 'pending' && (
                      <div className="p-5 bg-amber-50/50 border-t border-slate-200">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Review Actions</h4>
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-slate-600 mb-1">Feedback (optional)</label>
                          <textarea
                            value={feedback[sub.id] || ''}
                            onChange={e => setFeedback(prev => ({ ...prev, [sub.id]: e.target.value }))}
                            placeholder="Provide feedback to the author..."
                            className="w-full border border-slate-200 rounded-sm p-2.5 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
                            data-testid={`feedback-${index}`}
                          />
                        </div>
                        <div className="flex space-x-3">
                          <Button
                            onClick={() => handleReview(sub.id, 'approved')}
                            disabled={processing[sub.id]}
                            className="bg-green-600 hover:bg-green-700 rounded-sm"
                            data-testid={`approve-${index}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            {processing[sub.id] ? 'Processing...' : 'Approve & Publish'}
                          </Button>
                          <Button
                            onClick={() => handleReview(sub.id, 'declined')}
                            disabled={processing[sub.id]}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm"
                            data-testid={`decline-${index}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Decline
                          </Button>
                        </div>
                      </div>
                    )}

                    {sub.feedback && (
                      <div className="p-4 bg-blue-50 border-t border-slate-200 text-sm">
                        <p className="font-medium text-blue-700">Reviewer Feedback:</p>
                        <p className="text-blue-600 mt-1">{sub.feedback}</p>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BlogReviewPage;
